import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { endOfDay, endOfQuarter, format, isSameMonth, parse } from 'date-fns';
import type { MouseEvent } from 'react';
import { useId, useState } from 'react';

import {
  BUTTON_SX,
  FILTER_CONTROL_ACTIVE_SX,
  FILTER_CONTROL_DISABLED_SX,
  FILTER_CONTROL_SX,
} from '~/theme';

import PeriodGrid from './PeriodGrid';
import { type PeriodUnit, reducePeriodClick } from './periodRange';
import type { Granularity, SelectedDateRange } from './types';

const EMPTY_RANGE: SelectedDateRange = { start: null, end: null };
const DEFAULT_GRANULARITIES: Granularity[] = ['month', 'quarter'];

/**
 * Callers (the URL, localStorage) round-trip `end` through a `yyyy-MM-dd`
 * string, which parses back as local midnight, not end-of-day. Every
 * predicate in this component and periodRange.ts assumes an applied
 * month/quarter's `end` sits at 23:59:59.999, the same precision the click
 * reducer always produces internally — so a restored value is normalized
 * once here rather than loosening those predicates to accept either.
 */
function normalizeIncomingRange(range: SelectedDateRange): SelectedDateRange {
  if (!range.start || !range.end) {
    return range;
  }
  return { start: range.start, end: endOfDay(range.end) };
}

type MonthRangePickerProps = {
  placeHolder: string;
  onSelect: (selectedRange: SelectedDateRange) => void;
  value?: SelectedDateRange;
  maxDate?: Date;
  granularities?: Granularity[];
  /** Shown, but inert — for pages that can't scope themselves to a month range. */
  disabled?: boolean;
};

export const parseYearMonthDay = (dateStr: string): Date => {
  return parse(dateStr, 'yyyy-MM-dd', new Date());
};

const lastDateCurrentQuarter = (): Date => {
  return endOfQuarter(new Date());
};

function MonthRangePicker({
  placeHolder,
  onSelect,
  value,
  maxDate,
  granularities = DEFAULT_GRANULARITIES,
  disabled,
}: MonthRangePickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [appliedRange, setAppliedRange] = useState<SelectedDateRange>(
    normalizeIncomingRange(value ?? EMPTY_RANGE),
  );
  const [draftRange, setDraftRange] = useState<SelectedDateRange>(appliedRange);
  const [granularity, setGranularity] = useState<Granularity>(granularities[0]);
  const [viewYear, setViewYear] = useState<number>(
    appliedRange.start ? appliedRange.start.getFullYear() : new Date().getFullYear(),
  );
  const [prevValue, setPrevValue] = useState(value);

  // Sync the applied range from a parent-controlled `value` during render
  // rather than in an effect, so a new value doesn't briefly flash the old
  // applied range before committing.
  if (value && value !== prevValue) {
    setPrevValue(value);
    setAppliedRange(normalizeIncomingRange(value));
  }

  // If `granularities` narrows at runtime past the currently selected tab
  // (e.g. ['month', 'quarter'] -> ['quarter']), fall back to the new list's
  // first entry rather than rendering a grid for a tab that's gone.
  if (!granularities.includes(granularity)) {
    setGranularity(granularities[0]);
  }

  const effectiveMaxDate = maxDate ?? lastDateCurrentQuarter();
  const open = Boolean(anchorEl);
  // Unique per instance. Fixed strings are harmless while only the global
  // filter bar renders one, but the report's compare bar renders two side by
  // side, and duplicate DOM ids break both the label association and
  // `aria-controls` for whichever one loses.
  const buttonId = useId();
  const popoverId = useId();
  const id = open ? popoverId : undefined;

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setDraftRange(appliedRange);
    setGranularity(granularities[0]);
    setViewYear(appliedRange.start ? appliedRange.start.getFullYear() : new Date().getFullYear());
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleApply = () => {
    onSelect(draftRange);
    setAppliedRange(draftRange);
    handleClose();
  };

  const handleReset = () => {
    setDraftRange(appliedRange);
    setViewYear(appliedRange.start ? appliedRange.start.getFullYear() : new Date().getFullYear());
  };

  const handleSelectUnit = (unit: PeriodUnit) => {
    setDraftRange((prev) => reducePeriodClick(prev, unit));
  };

  const dateRangeText = (() => {
    if (!appliedRange.start || !appliedRange.end) {
      return '';
    }

    const formattedStart = format(appliedRange.start, 'MMM yyyy');

    if (isSameMonth(appliedRange.start, appliedRange.end)) {
      return formattedStart;
    }

    return `${formattedStart} to ${format(appliedRange.end, 'MMM yyyy')}`;
  })();

  return (
    <>
      <Button
        id={buttonId}
        aria-controls={id}
        aria-haspopup="dialog"
        aria-expanded={open}
        variant="outlined"
        onClick={handleOpen}
        disabled={disabled}
        sx={{
          ...FILTER_CONTROL_SX,
          // Always the active treatment: a period is always applied, so unlike the dropdowns
          // there is no "All" resting state to distinguish from. The exception is a disabled
          // control, which holds no range at all.
          ...(disabled ? FILTER_CONTROL_DISABLED_SX : FILTER_CONTROL_ACTIVE_SX),
          whiteSpace: 'nowrap',
          pl: 1.75,
          pr: 0.875,
          // The same 200px the dropdowns take, as a minimum rather than a fixed width: a
          // two-month range is wider than 200 and must not be clipped, and it stops the control
          // collapsing to its label when it holds no range.
          minWidth: 200,
          justifyContent: 'flex-start',
        }}
      >
        {dateRangeText ? (
          <>
            <Box sx={{ color: 'text.secondary', paddingRight: '5px', fontWeight: 400 }}>
              {placeHolder}
            </Box>
            <Box sx={{ paddingRight: '5px', fontWeight: 500 }}>{dateRangeText}</Box>
          </>
        ) : (
          placeHolder
        )}
        <ArrowDropDownIcon sx={{ color: 'inherit', opacity: 0.6, ml: 'auto' }} />
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disablePortal
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': 'Select period',
            sx: { minWidth: 420, maxWidth: 480 },
          },
        }}
      >
        {granularities.length > 1 && (
          <Tabs
            value={granularity}
            onChange={(_, next: Granularity) => {
              setGranularity(next);
              // A month draft and a quarter draft can't share one range without
              // one end landing mid-unit, so switching units drops the draft
              // rather than carrying a mixed-granularity range forward.
              setDraftRange(EMPTY_RANGE);
            }}
            aria-label="Period granularity"
            variant="fullWidth"
          >
            {/* Only rendered when granularities.length > 1, which with a
                two-member Granularity type means both tabs are always
                present, so there is no disabled case to render here. */}
            <Tab value="month" label="Month" sx={{ textTransform: 'none' }} />
            <Tab value="quarter" label="Quarter" sx={{ textTransform: 'none' }} />
          </Tabs>
        )}

        <PeriodGrid
          granularity={granularity}
          year={viewYear}
          onYearChange={setViewYear}
          range={draftRange}
          maxDate={effectiveMaxDate}
          onSelectUnit={handleSelectUnit}
        />

        <Divider />

        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} p={1.5}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            data-testid="period-picker-cancel-btn"
            sx={BUTTON_SX}
          >
            Cancel
          </Button>
          <Box display="flex" gap={1}>
            <Button onClick={handleReset} data-testid="period-picker-reset-btn" sx={BUTTON_SX}>
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleApply}
              data-testid="period-picker-apply-btn"
              sx={BUTTON_SX}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

export default MonthRangePicker;
